import { useEffect } from 'react';
import { usePostOfficeSideNavMessages } from '@post-office/placements--growth/confluence-side-nav/contexts/post-office-side-nav-message-provider';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { useLoomAiMeetingNotesSideNavCardState } from './useLoomAiMeetingNotesSideNavCard.ts';

const logger = getLogger('useLoomAiMeetingNotesSideNavCardListener');

const MESSAGE_ID = 'LOOM_AI_MEETING_NOTES';

interface UseLoomAiMeetingNotesSideNavCardListenerOptions {
	readFromLocalStorage?: boolean;
}

export const useLoomAiMeetingNotesSideNavCardListener = ({
	readFromLocalStorage = false,
}: UseLoomAiMeetingNotesSideNavCardListenerOptions) => {
	const context = usePostOfficeSideNavMessages();
	const { isSideNavCardEligible } = useLoomAiMeetingNotesSideNavCardState();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	/**
	 * This is required due to selecting a template refreshing the page OR opening in a new tab,
	 * the local storage value being set suggests a user very recently had clicked on the meeting notes template
	 */
	useEffect(() => {
		/**
		 * If the card is eligible then we don't want to use the local storage value (local storage = true and not eligible suggests refresh or new tab)
		 */
		if (
			!fg('loom_ai_meeting_notes_in_confluence_killswitch') ||
			!readFromLocalStorage ||
			!context ||
			isSideNavCardEligible
		) {
			return;
		}

		let hasClickedOnTemplate = false;
		try {
			hasClickedOnTemplate = localStorage.getItemAsBoolean(
				keys.USER_HAS_CLICKED_ON_MEETING_NOTES_TEMPLATE,
			);
		} catch (getError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'fail',
					actionSubject: 'loomAiMeetingNotesInConfluence.getLocalStorageItem',
					source: 'confluenceLoomSideNavBannerCard',
				},
			});
			logger.error`An error occurred while accessing local storage: ${getError}`;
			return;
		}

		if (!hasClickedOnTemplate) {
			return;
		}

		logger.info`Local storage suggests user has recently clicked on an eligible meeting notes template, enabling message ${MESSAGE_ID}`;
		context.enablePostOfficeSideNavMessage(MESSAGE_ID);
	}, [context, isSideNavCardEligible, readFromLocalStorage, createAnalyticsEvent]);

	useEffect(() => {
		if (!fg('loom_ai_meeting_notes_in_confluence_killswitch')) {
			return;
		}
		/**
		 * This should only ever be reached if {@link useLoomAiMeetingNotesSideNavCardListener}
		 * is consumed in an incorrect location without the provider
		 */
		if (!context) {
			return;
		}

		if (isSideNavCardEligible) {
			logger.info`User has clicked on an eligible meeting notes template, enabling message ${MESSAGE_ID}`;
			context.enablePostOfficeSideNavMessage(MESSAGE_ID);
		}
	}, [isSideNavCardEligible, context]);
};
